import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from "@angular/fire/compat/firestore";
import {Observable, Subscription} from "rxjs";
import { Centre } from "../../../../interfaces/Centres";
import { CentreService } from "../../../../services/centre.service";
import { FullChild } from "../../../../interfaces/FullChild";
import * as XLSX from "xlsx";
import { MatSnackBar } from "@angular/material/snack-bar";
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import {FinancialYearService} from "../../../../services/financial-year.service";

interface CentreData {
  centre: string;
  count: number;
}

interface StateData {
  state: string;
  centres: CentreData[];
  total?: number; // Optional field for total count
}

@Component({
  selector: 'app-location-state-report',
  templateUrl: './location-state-report.component.html',
  styleUrls: ['./location-state-report.component.scss']
})
export class LocationStateReportComponent implements OnInit, OnDestroy {
  private searchSubscription: Subscription | null = null;
  mData: any[] = [];
  tableData: StateData[] = [];
  uniqueCentres: string[] = [];
  startDate: Date | undefined | null;
  endDate: Date | undefined | null;
  fileName = 'State_Count_Report.xlsx';
  selectedCentreCode: any = 'All';
  centres$: Observable<Centre[]>;

  selectedCentre:any;
  displayCentre = (c: any) => c?.centreName;
  isLoading = false;  // New loading flag
  isButtonDisabled: boolean = false;

  constructor(private mFirestore: AngularFirestore,
              private mCentreService: CentreService,
              private readonly financialYearService: FinancialYearService,
              private mSnackbar: MatSnackBar) {
    this.centres$ = this.mCentreService.getAllCentres();
  }

  ngOnInit() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
  }

  onCentreSelect(selectedCentre: Centre) {
    if (!selectedCentre) return;

    console.log("Selected Centre:", selectedCentre);

    this.selectedCentre = selectedCentre;

    // // 1️⃣ Set centre details in form
    // this.studentForm.patchValue({
    //   centreName: selectedCentre.centreName,
    //   centreCode: selectedCentre.centreCode
    // });
  }

  clearAll() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.mData = [];
    this.selectedCentre=[];
    this.tableData = [];
    this.uniqueCentres = [];
  }

  search() {
    // Unsubscribe from any previous search to prevent data accumulation
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    // Clear previous search results
    this.clearPreviousSearch();
    this.isLoading = true;  // Start loading
    this.isButtonDisabled = true;
    // Clear previous data to avoid accumulation of results
    this.mData = []; // Clear previous data

    if (this.startDate != null && this.endDate != null) {

      this.searchSubscription = this.mFirestore.collection<FullChild>('ALChild', ref => ref.orderBy('centreName'))
          .get()
        .subscribe(res => {
          let childbyadmission: FullChild[] = [];

        let d =res.docs.map(x=>x.data())  // Process admission data
          d.forEach(it => {
            it.admissionData.forEach((c, i) => {
              if (i % 2 == 0) { // Handles alternating admission/follow-up entries
                if (c.dateOfAdmission?.toDate()!! >= this.startDate!! && c.dateOfAdmission?.toDate()!! <= this.endDate!!) {
                  childbyadmission.push(it); // Push child within date range
                }
              }
            });
          });

          // If "All Centre" is selected, include all centres
          if (this.selectedCentreCode !== "All") {
            childbyadmission = childbyadmission.filter(child => child.centreCode === this.selectedCentreCode);
          }

          let data2: any[] = [];
          let uniquestates: Set<string> = new Set();

          // Store data with state, centre, and type ('Follow Up' or 'Unique')
          childbyadmission.forEach(child => {
            let type = child?.admissionData.length > 2 ? 'Follow Up' : 'Unique';

            let obj = {
              state: child.state,
              centre: child.centreName,
              type: type,
              childCode: child.childCode
            };
            this.mData.push(obj);
            uniquestates.add(child.state);
          });

          // Sort states alphabetically
          const sortedstates = Array.from(uniquestates).sort();
          this.uniqueCentres = [...new Set(this.mData.map(child => child.centre))];

          // Initialize centre and state totals
          let centreTotals: { [key: string]: number } = {};
          let stateTotals: { [key: string]: number } = {};
          let totalRow: any[] = []; // Stores total for each row (i.e., state)
          let totalColumn: any = {}; // Stores total for each column (i.e., centre)

          // Initialize total column values
          this.uniqueCentres.forEach(centre => {
            totalColumn[centre] = 0;
          });

          let grandTotal = 0; // Grand total (for both rows and columns)

          // Process data to build the table
          sortedstates.forEach(state => {
            let stateData: any[] = [];
            let rowTotal = 0; // Total for this state (row)

            this.uniqueCentres.forEach(centre => {
              let count = this.mData.filter((child) => child.state === state && child.centre === centre).length;
              rowTotal += count; // Add to row total for state
              totalColumn[centre] += count; // Add to the column total for centre
              stateData.push({
                state: state,
                centre: centre,
                count: count
              });
            });

            // Push the row total to stateTotals
            stateTotals[state] = rowTotal;
            totalRow.push({ state: state, centre: 'xTotal', count: rowTotal }); // Row total for this state
            grandTotal += rowTotal; // Add row total to grand total

            data2 = data2.concat(stateData);
          });

          // Add row totals (column 'Total') for each state
          totalRow.forEach(row => {
            data2.push(row); // Add state total to the data
          });

          // Add column totals (last row: 'Total' row for each centre)
          this.uniqueCentres.forEach(centre => {
            data2.push({
              state: 'xTotal',
              centre: 'xTotal',
              count: totalColumn[centre] // Column total for this centre
            });
          });

          // Add final grand total at bottom-right (last cell with "Total" in both state and centre fields)
          data2.push({ state: 'xTotal', centre: 'xTotal', count: grandTotal });

          // Process tableData to display
          this.tableData = data2.reduce((acc, curr) => {
            const state = acc.find((item: any) => item.state === curr.state);
            if (state) {
              state.centres.push({ centre: curr.centre, count: curr.count });
            } else {
              acc.push({
                state: curr.state,
                centres: [{ centre: curr.centre, count: curr.count }]
              });
            }
            return acc;
          }, []);

          // Sort tableData by state name
          this.tableData = this.tableData.sort((a, b) => a.state.localeCompare(b.state));

          this.isLoading = false;  // Stop loading
          this.isButtonDisabled = false;
        });
    } else {
      this.mSnackbar.open('Please select a proper date range first', '', {
        panelClass: 'error',
        duration: 3000
      });
    }
  }



  clearPreviousSearch() {
    this.mData = [];
    this.tableData = [];
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  exportexcel() {
    let element = document.getElementById('excel-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileName);
  }

  getTotalForCentre(centre: string): number {
    return this.tableData.reduce((acc, stateData) => {
      const centreData = stateData.centres.find(c => c.centre === centre);
      return acc + (centreData ? centreData.count : 0);
    }, 0);
  }
  onEndDateChanged() {
    this.endDate?.setHours(23, 59, 0, 0);
  }

  protected readonly PermissionType = PermissionType;
}
